import {Alert, Button, IconButton, InputBase, Paper, styled, Tooltip, Typography} from "@mui/material";
import {useDispatch} from "react-redux";
import React, {useState} from "react";
import {ApiUrl} from "../utils/portal-urls";
import {handleHttpResponse} from "../utils/http-helpers";
import {addLatestBatch, batchOnRefresh} from "../features/allBatchesSlice";
import {StyledDivider} from "../NuccSpecialtyValueSet/NuccSpecialtyValueSetPagePasteURLPanel";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import DownloadIcon from "@mui/icons-material/Download";
import ShowSnackbar from "../utils/ShowSnackbar";
import {CustomBackdrop} from "../utils/customStyles";

/**
 * Starts an import for one Amendment #1 A5 value set.
 *
 * Differs from the older paste-URL panels on purpose: the primary action sends **no URL**, because
 * the server derives the download address from the artifact's own canonical. Those panels make the
 * operator paste a URL every time and store it as an editable setting, which is a step that can
 * silently point an import at the wrong artifact. The override field is still here for a pre-release
 * or locally hosted draft — it is just no longer the default path.
 *
 * @date: 08/11/26
 * @copy: (C) Copyright 2026 MaxMD Corporation, All Rights Reserved.
 */

const StyledPaper = styled(Paper)({
    padding: '2px 4px',
    display: 'flex',
    alignItems: 'center',
    marginTop: '10px',
})

export default function TerminologyImportPanel(props) {
    const terminology = props.terminology;
    const dispatch = useDispatch();
    const [overrideUrl, setOverrideUrl] = useState('');
    const [snackbarMsg, setSnackbarMsg] = useState({open: false, msg: '', msgLv: ''});
    const [loading, setLoading] = useState(false);

    const startImport = async (url) => {
        const requestOptions = {
            method: 'POST',
            headers: {'Content-Type': 'text/plain'},
        };
        // Only send a body when overriding. An empty body is what tells the server to use the
        // artifact's canonical.
        if (url) {
            requestOptions.body = url;
        }

        try {
            setLoading(true);
            const res = await fetch(ApiUrl.ExternalSource.importTerminology.getPath(terminology.key), requestOptions);
            const data = await handleHttpResponse(res);
            dispatch(addLatestBatch({type: terminology.key, id: data.id, batch: data}));
            dispatch(batchOnRefresh());
            setSnackbarMsg({open: true, msg: `Started ${terminology.label} import`, msgLv: 'success'});
        } catch (err) {
            console.error(`Failed to import ${terminology.key}: ` + err);
            setSnackbarMsg({open: true, msg: `Failed to start ${terminology.label} import: ${err}`, msgLv: 'error'});
        } finally {
            setLoading(false);
        }
    }

    const handleSubmit = (event) => {
        event.preventDefault();
        startImport(event.target.url.value);
    }

    return (
        <div style={{marginBottom: '10px'}}>
            {terminology.notice &&
                <Alert severity='warning' sx={{mb: 2}}>{terminology.notice}</Alert>}

            <Button
                variant='contained'
                endIcon={<DownloadIcon/>}
                onClick={() => startImport('')}
            >
                Import from published artifact
            </Button>
            <Typography variant='caption' display='block' sx={{mt: 1, color: 'text.secondary'}}>
                Fetches the value set from its own canonical URL. Use the field below only to import a
                copy hosted elsewhere.
            </Typography>

            <StyledPaper component="form" elevation={3} onSubmit={handleSubmit}>
                <InputBase
                    sx={{ml: 1, flex: 1}}
                    value={overrideUrl}
                    name="url"
                    placeholder="Override URL (optional)"
                    autoComplete="off"
                    onChange={(e) => setOverrideUrl(e.target.value)}
                />
                <StyledDivider orientation="vertical"/>
                <Tooltip title="Import from this URL instead" arrow>
                    <span>
                        <IconButton type="submit" sx={{p: '10px'}} aria-label="import" disabled={!overrideUrl}>
                            <KeyboardReturnIcon color={overrideUrl ? 'primary' : 'disabled'}/>
                        </IconButton>
                    </span>
                </Tooltip>
            </StyledPaper>

            <ShowSnackbar open={snackbarMsg.open} close={(c) => setSnackbarMsg(c)} msg={snackbarMsg}/>
            <CustomBackdrop open={loading}/>
        </div>
    )
}
